import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { formatCurrency } from "@theme/utilities";

/**
 * Progress Bar Component
 *
 * Reusable progress bar component that can be used anywhere
 * Later can be moved to separate file
 */
class ProgressBar extends Component {
  static get observedAttributes() {
    return ["percent", "label"];
  }

  #isReady = false;

  connectedCallback() {
    super.connectedCallback();
    this.#updateDisplay();
    this.#isReady = true;

    // Dispatch ready event
    this.dispatchEvent(
      new CustomEvent("progress-bar-ready", {
        bubbles: true,
        detail: { element: this },
      })
    );
  }

  attributeChangedCallback() {
    if (this.#isReady) {
      this.#updateDisplay();
    }
  }

  /**
   * Update progress bar display
   */
  #updateDisplay() {
    const percent = this.getAttribute("percent") || "0";
    const label = this.getAttribute("label") || "";

    // Update progress fill
    const fill = this.querySelector(".progress-bar__fill");
    if (fill) {
      const percentValue = Math.min(Math.max(parseFloat(percent), 0), 100);
      fill.style.setProperty("--percent", `${percentValue}%`);
    }

    // Update label if present
    const labelElement = this.querySelector(".progress-bar__label");
    if (labelElement && label) {
      labelElement.textContent = label;
    }
  }

  /**
   * Update progress programmatically
   * @param {number|string} percent - Progress percentage
   * @param {string} label - Optional label
   */
  update(percent, label = "") {
    this.setAttribute("percent", percent.toString());
    if (label) {
      this.setAttribute("label", label);
    }
  }
}

/**
 * Free Shipping Goal Component
 *
 * Displays progress towards the free-shipping threshold and, when Cart BOGO
 * is active, a second stage/checkpoint for the Cart-BOGO free-gift
 * threshold in the SAME bar (the smaller of the two thresholds is always
 * "stage 1" / the checkpoint, the larger is "stage 2" / the end of the
 * bar) - integrated per Ringo-Feedback 2026-09-19. Updates in real-time
 * when the cart changes.
 *
 * Supports currency conversion for the free-shipping threshold only (both
 * single-currency with conversion and multi-currency formats). The
 * Cart-BOGO threshold is intentionally compared without conversion, to
 * stay consistent with assets/cart-bogo.js, which does the same.
 *
 * @extends {Component}
 */
export class FreeShippingGoal extends Component {
  #progressBarElement = null;

  connectedCallback() {
    super.connectedCallback();

    // Listen to cart targets
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener("progress-bar-ready", this.#handleProgressBarReady);

    // Initialize on load
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener("progress-bar-ready", this.#handleProgressBarReady);
  }

  /**
   * Handle progress bar ready event
   * @param {CustomEvent} event - Progress bar ready event
   */
  #handleProgressBarReady = (event) => {
    // Check if this progress bar belongs to us
    if (event.detail.element.closest("free-shipping-goal") === this) {
      this.#progressBarElement = event.detail.element;
    }
  };

  /**
   * Handle cart update events
   * @param {CustomEvent} event - Cart update event
   */
  #handleCartUpdate = (event) => {
    const { data } = event.detail;

    // Update component data from cart response
    if (data.sections) {
      this.#updateCartTotalFromEvent(data);

      // Re-setup currency conversion in case currency changed
      this.#setupCurrencyConversion();

      // Update display immediately after data extraction
      this.#updateDisplay();
    }
  };

  /**
   * Initialize the component
   */
  #initialize() {
    this.#setupCurrencyConversion();

    // Try to get progress bar element immediately
    this.#progressBarElement = this.querySelector("progress-bar");

    requestAnimationFrame(() => {
      this.#updateDisplay();
    });
  }

  /**
   * Update cart data from cart update event
   * @param {Object} cartData - Cart update event data
   */
  #updateCartTotalFromEvent(cartData) {
    if (!cartData.sections) return;

    const sectionId = this.closest("[data-section-id]")?.dataset.sectionId;
    if (!sectionId || !cartData.sections[sectionId]) return;

    const newSectionHTML = new DOMParser().parseFromString(
      cartData.sections[sectionId],
      "text/html"
    );
    const newFreeShippingGoal = newSectionHTML.querySelector("free-shipping-goal");

    if (!newFreeShippingGoal) return;

    // Update all relevant data attributes
    const newData = {
      cartTotal: newFreeShippingGoal.dataset.cartTotal,
      currentCurrency: newFreeShippingGoal.dataset.currentCurrency,
      minimumAmount: newFreeShippingGoal.dataset.minimumAmount,
      isSingleCurrency: newFreeShippingGoal.dataset.isSingleCurrency,
      shopCurrency: newFreeShippingGoal.dataset.shopCurrency,
      twoStage: newFreeShippingGoal.dataset.twoStage,
      stage1Type: newFreeShippingGoal.dataset.stage1Type,
      stage2Type: newFreeShippingGoal.dataset.stage2Type,
      bogoThreshold: newFreeShippingGoal.dataset.bogoThreshold,
    };

    // Only update if data actually changed to avoid unnecessary re-renders
    let hasChanges = false;
    for (const [key, value] of Object.entries(newData)) {
      if (this.dataset[key] !== value) {
        this.dataset[key] = value;
        hasChanges = true;
      }
    }

    // Fresh markup (congratulations/remaining text blocks) also needs to be
    // taken over on morph-opt-out elements, since e.g. the Cart-BOGO gift
    // product title only lives in the rendered HTML, not in a data
    // attribute. Cheap innerHTML swap of just the text blocks, not the
    // whole element, so the <progress-bar>/checkpoint markup + listeners
    // stay untouched.
    const newMsgBlocks = newFreeShippingGoal.querySelectorAll("[data-goal-msg]");
    newMsgBlocks.forEach((newBlock) => {
      const currentBlock = this.querySelector(`[data-goal-msg="${newBlock.dataset.goalMsg}"]`);
      if (currentBlock && currentBlock.innerHTML !== newBlock.innerHTML) {
        currentBlock.innerHTML = newBlock.innerHTML;
        hasChanges = true;
      }
    });

    // Data updated successfully
  }

  /**
   * Setup currency conversion logic (free-shipping threshold only)
   */
  #setupCurrencyConversion() {
    const minimumAmount = parseFloat(this.dataset.minimumAmount);
    const isSingleCurrency = this.dataset.isSingleCurrency === "true";
    const shopCurrency = this.dataset.shopCurrency;
    const currentCurrency = this.dataset.currentCurrency;

    // Validate minimum amount
    if (isNaN(minimumAmount) || minimumAmount <= 0) {
      console.warn("FreeShippingGoal: Invalid minimum amount", this.dataset.minimumAmount);
      this.convertedAmountInCents = 0;
      return;
    }

    // Cache key for conversion to avoid recalculation
    const cacheKey = `${minimumAmount}-${currentCurrency}-${window.Shopify?.currency?.rate || 1}`;

    if (this.conversionCache?.key === cacheKey) {
      return; // Use cached value
    }

    // If single currency and current currency differs from shop currency
    if (isSingleCurrency && currentCurrency !== shopCurrency) {
      // Use Shopify currency rate for conversion
      if (window.Shopify?.currency?.rate) {
        const rate = parseFloat(window.Shopify.currency.rate);
        if (!isNaN(rate) && rate > 0) {
          this.convertedAmount = minimumAmount * rate;
          this.convertedAmountInCents = Math.round(this.convertedAmount * 100);
        } else {
          console.warn("FreeShippingGoal: Invalid currency rate", window.Shopify.currency.rate);
          this.convertedAmountInCents = Math.round(minimumAmount * 100);
        }
      } else {
        // Fallback if no rate available
        this.convertedAmountInCents = Math.round(minimumAmount * 100);
      }
    } else {
      // No conversion needed
      this.convertedAmountInCents = Math.round(minimumAmount * 100);
    }

    // Cache the conversion result
    this.conversionCache = {
      key: cacheKey,
      convertedAmountInCents: this.convertedAmountInCents,
      minimumAmount,
      isSingleCurrency,
      shopCurrency,
      currentCurrency,
      rate: window.Shopify?.currency?.rate,
    };
  }

  /**
   * Cents for the Cart-BOGO threshold - intentionally NOT currency
   * converted, see class doc comment.
   */
  get #bogoCents() {
    const value = parseFloat(this.dataset.bogoThreshold || "0");
    if (isNaN(value) || value <= 0) return 0;
    return Math.round(value * 100);
  }

  /**
   * @param {string} type - "shipping" or "bogo"
   * @returns {number} threshold in cents for that stage type
   */
  #stageCents(type) {
    return type === "bogo" ? this.#bogoCents : this.convertedAmountInCents || 0;
  }

  /**
   * Update the display based on current cart state
   */
  #updateDisplay() {
    const cartTotal = parseInt(this.dataset.cartTotal);

    // Validate cart total
    if (isNaN(cartTotal) || cartTotal < 0) {
      console.warn("FreeShippingGoal: Invalid cart total", this.dataset.cartTotal);
      return;
    }

    const twoStage = this.dataset.twoStage === "true";
    const stage1Type = this.dataset.stage1Type || "shipping";
    const stage2Type = this.dataset.stage2Type || "shipping";
    const stage1Cents = this.#stageCents(stage1Type);
    const stage2Cents = this.#stageCents(stage2Type);

    // Don't show if the final (stage 2) threshold is invalid
    if (stage2Cents <= 0) {
      return;
    }

    this.#toggleStates(cartTotal, twoStage, stage1Cents, stage2Cents);
  }

  /**
   * Get debug information for troubleshooting
   * @returns {Object} Debug information
   */
  getDebugInfo() {
    return {
      cartTotal: parseInt(this.dataset.cartTotal),
      convertedAmountInCents: this.convertedAmountInCents,
      bogoCents: this.#bogoCents,
      twoStage: this.dataset.twoStage === "true",
      stage1Type: this.dataset.stage1Type,
      stage2Type: this.dataset.stage2Type,
      moneyFormat: window.FoxTheme?.moneyFormat || window.Shopify?.currency?.money_format,
      conversionInfo: this.conversionInfo,
    };
  }

  /**
   * @param {number} amountCents
   * @returns {string} formatted money string
   */
  #formatAmount(amountCents) {
    const moneyFormat = window.FoxTheme?.moneyFormat || window.Shopify?.currency?.money_format;
    return formatCurrency(amountCents, moneyFormat);
  }

  /**
   * Update progress bar fill towards the final (stage 2) threshold
   * @param {number} cartTotal - Current cart total in cents
   * @param {number} finalCents - Stage 2 threshold in cents
   */
  #updateProgressBar(cartTotal, finalCents) {
    const progressBarElement = this.#progressBarElement || this.querySelector("progress-bar");
    if (!progressBarElement || finalCents <= 0) return;

    const percent = Math.min((cartTotal / finalCents) * 100, 100);
    progressBarElement.setAttribute("percent", percent.toString());
  }

  /**
   * Toggle between the three text states (stage1 remaining / stage2
   * remaining / all done), update the remaining-amount numbers, and
   * update the progress bar fill + checkpoint state.
   * @param {number} cartTotal - Current cart total in cents
   * @param {boolean} twoStage - Whether Cart BOGO is integrated as stage 2
   * @param {number} stage1Cents - Stage 1 (nearer) threshold in cents
   * @param {number} stage2Cents - Stage 2 (final) threshold in cents
   */
  #toggleStates(cartTotal, twoStage, stage1Cents, stage2Cents) {
    const stage1Reached = cartTotal >= stage1Cents;
    const stage2Reached = cartTotal >= stage2Cents;

    let state = "stage1";
    if (stage2Reached) {
      state = "done";
    } else if (twoStage && stage1Reached) {
      state = "stage2";
    }

    this.querySelectorAll("[data-goal-msg]").forEach((el) => {
      el.classList.toggle("hidden", el.dataset.goalMsg !== state);
    });

    const stage1AmountEl = this.querySelector('[data-goal-amount="stage1"]');
    if (stage1AmountEl) {
      const remaining = stage1Cents - cartTotal;
      if (remaining > 0) stage1AmountEl.textContent = this.#formatAmount(remaining);
    }

    const stage2AmountEl = this.querySelector('[data-goal-amount="stage2"]');
    if (stage2AmountEl) {
      const remaining = stage2Cents - cartTotal;
      if (remaining > 0) stage2AmountEl.textContent = this.#formatAmount(remaining);
    }

    // Bordeaux -> Gold + Truck-Badge-Scale, siehe mania-wellina-custom.css
    this.classList.toggle("is-reached", stage2Reached);
    this.classList.toggle("is-stage1-reached", stage1Reached);

    const checkpoint = this.querySelector(".progress-bar__checkpoint");
    if (checkpoint) {
      checkpoint.classList.toggle("is-reached", stage1Reached);
    }

    this.#updateProgressBar(cartTotal, stage2Cents);
  }
}

// Register components
customElements.define("progress-bar", ProgressBar);
customElements.define("free-shipping-goal", FreeShippingGoal);
